import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Link, useSearchParams } from "react-router-dom";
import { db } from "../firebase/firebase";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { useCart } from "../contexts/CartContext";
import { useSale } from "../contexts/SaleContext";
import { getDiscountedPrice, getDiscountBadge, getProductBehaviorType, PRODUCT_BEHAVIOR, getCartItemKey, getEffectiveUnitPrice, getFirstEnabledUnit, getUnitLabel } from "../utils/productUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCartShopping } from "@fortawesome/free-solid-svg-icons";

const PAGE_SIZE = 10;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const { addToCart } = useCart();
  const { sale, discountLabel, categoryDiscounts } = useSale();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("q") || "";

  // Discount helpers now come from productUtils (imported above)

  useEffect(() => {
    document.title = "Products | dawaacare";
  }, [])

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [searchQuery]);

  useEffect(() => {
    const productsRef = collection(db, "products");
    const q = query(productsRef, orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const productsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setProducts(productsData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching products:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <div >
      <Header />

      <div className="content products-page" style={{ margin: "140px 0px" }} data-aos="fade-up">
        <h1
          style={{
            textAlign: "center",
            margin: "30px 0px",
            fontSize: "42px",
            color: "#d2222d",
          }}
        >
          {searchQuery ? `Results for "${searchQuery}"` : "Our All Products"}
        </h1>

        {loading ? (
          <div className="products-container">
            {Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <div className="product-card skeleton-card" key={i}>
                <div className="skeleton-image-box skeleton-shimmer" />
                <div className="skeleton-info">
                  <div className="skeleton-line skeleton-shimmer" style={{ width: "50%", height: "12px" }} />
                  <div className="skeleton-line skeleton-shimmer" style={{ width: "80%", height: "16px", marginTop: "8px" }} />
                  <div className="skeleton-line skeleton-shimmer" style={{ width: "40%", height: "14px", marginTop: "10px" }} />
                </div>
              </div>
            ))}
          </div>
        ) : (() => {
          const filtered = products.filter(p =>
            !searchQuery || p.name?.toLowerCase().includes(searchQuery.toLowerCase())
          );
          const visible = filtered.slice(0, visibleCount);
          const hasMore = visibleCount < filtered.length;

          return (
            <>
              <div className="products-container">
                {visible.map((product) => (
                  <Link
                    to={`/product/${product.id}`}
                    style={{ textDecoration: "none" }}
                    key={product.id}
                  >
                    <div className="product-card">
                      <div className="card-image-box">
                        {getDiscountBadge(product, sale, discountLabel, categoryDiscounts) && (
                          <span className="discount-badge">{getDiscountBadge(product, sale, discountLabel, categoryDiscounts)}</span>
                        )}
                        {product.isOutOfStock && (
                          <span className="oos-overlay-badge">Out of Stock</span>
                        )}

                        <img src={product.picture} alt={product.name} />

                        {!product.isOutOfStock && (
                          <div className="add-to-cart-btn-wrapper">
                            <button
                              className="add-cart-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                const isMedicine = getProductBehaviorType(product.type) === PRODUCT_BEHAVIOR.MEDICINE;
                                const firstUnit  = isMedicine ? getFirstEnabledUnit(product) : null;
                                const cartKey    = getCartItemKey(product.id, product.type, firstUnit);
                                const unitLabel  = isMedicine ? ` (${getUnitLabel(product, firstUnit)})` : "";
                                const unitPrice  = getEffectiveUnitPrice(product, sale, discountLabel, firstUnit, categoryDiscounts);
                                addToCart({ ...product, id: cartKey, productId: product.id, name: product.name + unitLabel, selectedUnit: firstUnit, price: unitPrice });
                              }}
                              title="Add to cart"
                            >
                              <FontAwesomeIcon icon={faCartShopping} />
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="card-info">
                        <h3 className="product-type">{product.type}</h3>
                        <h4 className="product-name">{product.name}</h4>

                        <div className="price-container">
                          {(() => {
                            const isMedicine = getProductBehaviorType(product.type) === PRODUCT_BEHAVIOR.MEDICINE;
                            const firstUnit  = isMedicine ? getFirstEnabledUnit(product) : null;
                            const discounted = getDiscountedPrice(product, sale, discountLabel, firstUnit, categoryDiscounts);
                            const basePrice  = isMedicine
                              ? (product.sellingOptions?.[firstUnit]?.price ?? product.price)
                              : (product.retailPrice || product.price);
                            return discounted ? (
                              <>
                                <span className="new-price">Rs. {discounted}</span>
                                <span className="old-price">Rs. {basePrice}</span>
                              </>
                            ) : (
                              <span className="new-price">Rs. {basePrice}</span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {hasMore && (
                <div className="load-more-wrapper">
                  <button
                    className="load-more-btn"
                    onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                  >
                    Load More
                  </button>
                </div>
              )}
            </>
          );
        })()}
      </div>

      <Footer />
    </div>
  );
};

export default Products;